import { useState, useEffect } from 'react';
import { NoRecords, PageLoader } from '../../lib/loader';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { ArrowDown, ArrowUp, Edit } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import { useGetCoins, useUpdateFeeStatus } from './api/apiFeeManage';
import { useCheckAdminAccess } from '../WhiteIp/api/whiteipapi';
import { logout } from '../../hooks/logout';
import PermissionDenied from '../common/permissionDenied';
import { WaitingLoader } from '../../hooks/buttonLoad';
import { useBreadcrumbPagination } from '../../hooks/useBreadcrumbPagination';
import BreadcrumbPagination from '../../utils/BreadcrumbPagination';


function FeeManagement() {
    const navigate = useNavigate();
    const [tempPage, setTempPage] = useState(0);
    const [tempRows, setTempRows] = useState(25);
    const [sortOrder, setSortOrder] = useState('desc');
    const { data: getCoinList, isLoading, error } = useGetCoins({
        page: tempPage,
        size: tempRows,
        sortOrder: sortOrder,
        searchText: '',
        sortField: '_id',
    });

    const totalRecords = getCoinList?.data?.count || 0;

    const {
        page,
        rowsPerPage,
        totalPages,
        handleChangePage,
        handleChangeRowsPerPage
    } = useBreadcrumbPagination(totalRecords, tempRows);

    useEffect(() => {
        setTempPage(page);
    }, [page]);

    useEffect(() => {
        setTempRows(rowsPerPage);
    }, [rowsPerPage]);

    const updateCoinStatus = useUpdateFeeStatus();

    const handleEdit = (id) => {
        navigate(`/editCurrency/${id}`);
    };

    const coinStatus = async (item) => {

        await updateCoinStatus.mutateAsync({
            status: !item.status,
            _id: item._id,
        });
    };

    const checkAdminMutation = useCheckAdminAccess();
    const [access, setAccess] = useState(true);

    useEffect(() => {
        checkAdminAccess();
    }, []);

    const checkAdminAccess = async () => {
        try {
            const obj = { page: 'Fee Management' };
            const checkAdminRes = await checkAdminMutation.mutateAsync(obj);
            if (!checkAdminRes?.data?.status) {
                setAccess(false);
                logout();
            } else {
                setAccess(true);
            }
        } catch (error) {
            setAccess(false);
        }
    };

    const list = () => {
        if (isLoading) {
            return <PageLoader />;
        }
        if (error) {
            return <div>Error loading data: {error.message}</div>;
        }
        if (!getCoinList?.data?.crypto || getCoinList.data.crypto.length === 0) {
            return <NoRecords />;
        }
        return (
            <>
                {updateCoinStatus.isLoading &&
                    <PageLoader />}
                <div style={{ minHeight: '60vh' }}>
                    <Table sx={{ minWidth: 650 }} aria-label="fee management table">
                        <TableHead>
                            <TableRow>
                                <TableCell style={{ maxWidth: '60px' }} align="center">
                                    S.NO
                                </TableCell>
                                <TableCell align="center">Withdraw Fee (%)</TableCell>
                                <TableCell align="center">Min Internal Transfer</TableCell>
                                <TableCell align="center">Min Withdraw</TableCell>
                                <TableCell align="center">Coin Status</TableCell>
                                <TableCell align="center">
                                    Updated Date & Time{' '}
                                    {sortOrder !== 'desc' ? (
                                        <ArrowUp
                                            onClick={() => setSortOrder('desc')}
                                            style={{ cursor: 'pointer' }}
                                            aria-label="Sort descending"
                                        />
                                    ) : (
                                        <ArrowDown
                                            onClick={() => setSortOrder('asc')}
                                            style={{ cursor: 'pointer' }}
                                            aria-label="Sort ascending"
                                        />
                                    )}
                                </TableCell>
                                <TableCell align="center">Action</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {getCoinList.data.crypto.map((row, i) => (
                                <TableRow key={row._id}>
                                    <TableCell align="center">{page * rowsPerPage + (i + 1)}</TableCell>
                                    <TableCell className="text-nowrap" align="center">
                                        {row?.sendFee ? (
                                            <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '5px' }}>
                                                {row.sendFee}
                                                {row?.image && (
                                                    <img
                                                        src={row.image}
                                                        alt="Currency Icon"
                                                        style={{
                                                            height: '20px',
                                                            width: '20px',
                                                            borderRadius: '50%',
                                                            verticalAlign: 'middle',
                                                            objectFit: 'cover',
                                                        }}
                                                    />
                                                )}
                                            </span>
                                        ) : (
                                            '-'
                                        )}
                                    </TableCell>

                                    <TableCell className="text-nowrap" align="center">
                                        {row?.minInternalTransfer ? (
                                            <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '5px' }}>
                                                {row.minInternalTransfer}
                                                {row?.image && (
                                                    <img
                                                        src={row.image}
                                                        alt="Currency Icon"
                                                        style={{
                                                            height: '20px',
                                                            width: '20px',
                                                            borderRadius: '50%',
                                                            verticalAlign: 'middle',
                                                            objectFit: 'cover',
                                                        }}
                                                    />
                                                )}
                                            </span>
                                        ) : (
                                            '-'
                                        )}
                                    </TableCell>
                                    <TableCell className="text-nowrap" align="center">
                                        {row?.minTransfer ? (
                                            <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '5px' }}>
                                                {row.minTransfer}
                                                {row?.image && (
                                                    <img
                                                        src={row.image}
                                                        alt="Currency Icon"
                                                        style={{
                                                            height: '20px',
                                                            width: '20px',
                                                            borderRadius: '50%',
                                                            verticalAlign: 'middle',
                                                            objectFit: 'cover',
                                                        }}
                                                    />
                                                )}
                                            </span>
                                        ) : (
                                            '-'
                                        )}
                                    </TableCell>

                                    <TableCell align="center">
                                        <div className="form-check form-switch " style={{ marginLeft: '50px' }}>
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                role="switch"
                                                id={`status-switch-${row._id}`}
                                                checked={row.status}
                                                onChange={() => coinStatus(row)}
                                                aria-label={`Toggle status for ${row.name}`}
                                            />
                                        </div>
                                    </TableCell>
                                    <TableCell align="center">
                                        {moment(row.updatedAt).format('DD/MM/YYYY | hh:mm')}
                                    </TableCell>
                                    <TableCell align="center">
                                        <div className="d-flex justify-content-center">
                                            <Edit
                                                className="mx-3"
                                                size={20}
                                                type="button"
                                                onClick={() => handleEdit(row._id)}
                                                aria-label={`Edit ${row.name}`}
                                            />
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
                <BreadcrumbPagination
                    page={page}
                    rowsPerPage={rowsPerPage}
                    totalPages={totalPages}
                    totalRecords={totalRecords}
                    handleChangePage={handleChangePage}
                    handleChangeRowsPerPage={handleChangeRowsPerPage}
                />
            </>
        );
    };

    let content;

    if (isLoading) {
        content = <WaitingLoader />;
    } else if (access) {
        content = (
            <div>
                <h2>Crypto Fee Management</h2>
                <div className="ad_pg_bx bg-white">
                    <div className="d-flex justify-content-between align-items-center mb-2">

                    </div>
                    {list()}
                </div>
            </div>
        );
    } else {
        content = <PermissionDenied />;
    }

    return <>{content}</>;

}

export default FeeManagement;